import { router } from "@inertiajs/react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import OnboardingDefaultCharterBanner from "@/components/onboarding/onboarding-default-charter-banner"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { Skeleton } from "@/components/ui/skeleton"

/** A single numbered block — value title zipped onto its formal expansion. */
export interface FormalCharterValue {
  title: string
  expanded_body: string
}

/**
 * The formal charter payload — an Inertia OPTIONAL prop, so `undefined`
 * until the sheet-open partial reload (`router.reload({ only:
 * ['formal_charter'] })`) resolves. `values` are pre-zipped server-side
 * (title↔expansion, 1:1 ordered with the principle cards).
 */
export interface FormalCharter {
  intro: string
  values: FormalCharterValue[]
  what_we_ask: string[]
  what_you_get: string[]
  is_default: boolean
}

export interface OnboardingCharterSheetProps {
  brandName: string
  /** Undefined until the lazy `formal_charter` partial reload resolves. */
  formalCharter?: FormalCharter
  /**
   * Single acceptance (PRD §3): ticks the inline checkbox + returns focus
   * to Continue. The sheet does NOT auto-submit — the creator still taps
   * the one shipped Continue.
   */
  onAccept: () => void
  /**
   * Ordered focus candidates for an ACCEPT-originated close (OKS-895).
   * Resolved lazily, at close time, so the caller reports the DOM as it is
   * *after* the acceptance has been applied. The sheet focuses the first
   * candidate that actually accepts focus; if none does, it leaves the
   * default restore-to-trigger alone rather than stranding focus on
   * `<body>`. Omit to keep the default behaviour entirely.
   */
  resolveAcceptFocusCandidates?: () => readonly (HTMLElement | null)[]
}

/**
 * OKS-459 — The full-charter bottom-sheet (`ui/drawer`, Base UI `Drawer`
 * since OKS-1022 — NOT a shadcn Dialog). Opening it lazily loads the formal `charter_body` via an
 * Inertia partial reload; that boundary is the real trigger for the
 * loading-skeleton + error+retry states. Content is READ-ONLY — it is the
 * formal version being accepted, not an editor.
 *
 * Display state derives from props, not a fragile state machine:
 *   - `formalCharter` present → populated (3 numbered blocks + two lists)
 *   - else `errored` → actionable error + Retry (sheet stays open)
 *   - else → skeleton matching the populated layout
 *
 * The drawer slide is a CSS transition on the sheet; a
 * `prefers-reduced-motion` user gets no transition.
 *
 * FOCUS (OKS-1022; before that vaul left focus on the trigger while open,
 * see OKS-895): Base UI moves focus onto the sheet container on open and,
 * on close, runs `finalFocus`. A dismiss (Esc / overlay / swipe) keeps the
 * default restore to the "read the full charter" trigger; an accept hands
 * focus to the first accept candidate that takes it (see
 * `resolveCloseFocus`).
 */
export function OnboardingCharterSheet({
  brandName,
  formalCharter,
  onAccept,
  resolveAcceptFocusCandidates,
}: OnboardingCharterSheetProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [errored, setErrored] = useState(false)
  // Marks a close as accept-originated, so a dismiss (Esc / overlay / drag)
  // keeps the default restore-to-trigger (UX Spec §1).
  const acceptedCloseRef = useRef(false)
  // Guards against re-issuing the partial reload on every open once the
  // content is in flight / loaded; Retry + reopen still re-arm it.
  const inFlight = useRef(false)

  function loadCharter() {
    setErrored(false)
    inFlight.current = true
    router.reload({
      only: ["formal_charter"],
      onError: () => {
        inFlight.current = false
        setErrored(true)
      },
      onSuccess: () => {
        inFlight.current = false
        setErrored(false)
      },
    })
  }

  function handleOpenChange(next: boolean) {
    if (next) acceptedCloseRef.current = false
    setOpen(next)
    if (next && !formalCharter && !inFlight.current) {
      loadCharter()
    }
  }

  function handleAccept() {
    acceptedCloseRef.current = true
    setOpen(false)
    onAccept()
  }

  /**
   * OKS-895 / OKS-1022 — the single focus writer for a close, as Base UI's
   * `finalFocus` getter (resolved at close time).
   *
   * - Dismiss → `true`: Base UI's default restore to the trigger.
   * - Accept → the first candidate that *demonstrably* takes focus, returned
   *   so Base UI settles focus there. None landable → `true` (the trigger).
   *
   * It never returns `false`/`undefined`: Base UI reads those as "do not move
   * focus", which could leave it on `<body>` — worse than the behaviour
   * OKS-895 fixed (UX Spec §6).
   */
  function resolveCloseFocus(): HTMLElement | true {
    if (!acceptedCloseRef.current) return true
    acceptedCloseRef.current = false

    for (const candidate of resolveAcceptFocusCandidates?.() ?? []) {
      candidate?.focus()
      // Behavioural check, not a property check: a disabled/hidden control
      // silently ignores `.focus()`, and asking the DOM who actually holds
      // focus cannot be fooled by an assumption about which props are set.
      if (candidate && document.activeElement === candidate) {
        return candidate
      }
    }
    return true
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerTrigger
        data-testid="wizard-charter-read-full"
        className="text-primary focus-visible:ring-ring self-start text-sm font-medium underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none"
      >
        {t("wizard.charter.read_full_charter")}
      </DrawerTrigger>

      <DrawerContent
        data-testid="wizard-charter-sheet"
        finalFocus={resolveCloseFocus}
      >
        <div className="mx-auto flex w-full max-w-md flex-col overflow-hidden">
          <DrawerHeader className="text-left">
            <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              {t("wizard.charter.sheet.eyebrow")}
            </p>
            <DrawerTitle className="text-xl break-words">
              {t("wizard.charter.sheet.title", { brand: brandName })}
            </DrawerTitle>
          </DrawerHeader>

          <div className="max-h-[60vh] overflow-y-auto px-4 pb-4">
            {formalCharter ? (
              <CharterSheetBody charter={formalCharter} />
            ) : errored ? (
              <div
                data-testid="wizard-charter-sheet-error"
                className="flex flex-col gap-3 py-4"
              >
                <Alert variant="destructive">
                  <AlertDescription>
                    {t("wizard.charter.sheet.error")}
                  </AlertDescription>
                </Alert>
                <Button
                  type="button"
                  variant="outline"
                  onClick={loadCharter}
                  data-testid="wizard-charter-sheet-retry"
                  className="self-start"
                >
                  {t("wizard.charter.sheet.retry")}
                </Button>
              </div>
            ) : (
              <CharterSheetSkeleton
                label={t("wizard.charter.sheet.loading_label")}
              />
            )}
          </div>

          <DrawerFooter>
            <Button
              type="button"
              size="lg"
              className="w-full"
              disabled={!formalCharter}
              onClick={handleAccept}
              data-testid="wizard-charter-sheet-accept"
            >
              {t("wizard.charter.sheet.accept_action")}
            </Button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

/** Populated formal charter — intro, 3 numbered blocks, two lists. */
function CharterSheetBody({ charter }: { charter: FormalCharter }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-4">
      {charter.is_default && <OnboardingDefaultCharterBanner />}

      <p className="text-muted-foreground text-sm break-words">
        {charter.intro}
      </p>

      <div className="border-border border-t" />

      <ol className="flex flex-col gap-4">
        {charter.values.map((block, index) => (
          <li
            key={index}
            data-testid={`wizard-charter-sheet-block-${index}`}
            className="flex flex-col gap-1"
          >
            <p className="text-foreground text-sm font-semibold break-words">
              {index + 1} · {block.title}
            </p>
            <p className="text-muted-foreground text-sm break-words">
              {block.expanded_body}
            </p>
          </li>
        ))}
      </ol>

      <div className="border-border border-t" />

      <CharterSheetList
        testId="wizard-charter-sheet-ask"
        heading={t("wizard.charter.sheet.what_we_ask")}
        items={charter.what_we_ask}
      />
      <CharterSheetList
        testId="wizard-charter-sheet-get"
        heading={t("wizard.charter.sheet.what_you_get")}
        items={charter.what_you_get}
      />
    </div>
  )
}

function CharterSheetList({
  testId,
  heading,
  items,
}: {
  testId: string
  heading: string
  items: string[]
}) {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-foreground text-sm font-semibold">{heading}</p>
      <ul data-testid={testId} className="list-disc pl-5">
        {items.map((item, index) => (
          <li key={index} className="text-muted-foreground text-sm break-words">
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Loading skeleton matching the populated layout (NOT a spinner). */
function CharterSheetSkeleton({ label }: { label: string }) {
  return (
    <div
      data-testid="wizard-charter-sheet-skeleton"
      className="flex flex-col gap-4 py-2"
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <Skeleton className="h-4 w-3/4" />
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex flex-col gap-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
        </div>
      ))}
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  )
}

export default OnboardingCharterSheet
